/**
 * My Profile page component.
 *
 * S018 authority: Figma node 39:4695
 * Route: /account (authenticated)
 *
 * This page replaces the placeholder AccountPage with the canonical
 * S018 my-profile screen. The page:
 * - Renders h1 "Профиль" as the page title
 * - Wraps MyProfileView with the profile program
 * - Uses API port for profile data (production path)
 * - Uses existing production logout boundary
 * - Port is created once per mounted page instance
 */
import { useRef } from 'react';
import { MyProfileView } from './view';
import { useMyProfileProgram } from './useMyProfileProgram';
import { createProductionApiPort } from './adapters/apiMyProfilePort';
import type { MyProfilePort } from './port';

/**
 * My Profile page.
 * Wraps MyProfileView with the Elm-ish program hook.
 * Port is bound once per page instance to prevent recreation on re-renders.
 */
export function MyProfilePage() {
  // Create port once per mounted page instance
  const portRef = useRef<MyProfilePort | null>(null);
  if (!portRef.current) {
    portRef.current = createProductionApiPort();
  }

  const { model, retry, logout, clearLogoutError } = useMyProfileProgram(portRef.current);

  return (
    <div className="my-profile-page" data-testid="my-profile-page">
      <h1 className="page-title" data-testid="page-title">Профиль</h1>
      <MyProfileView
        model={model}
        onRetry={retry}
        onLogout={logout}
        onClearLogoutError={clearLogoutError}
      />
    </div>
  );
}
